<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="600">
        <v-card>
            <v-card-title id="heading" class="d-flex justify-space-between">
                <h4>Add Zone to "{{ location.name }}" Location</h4>
                <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue',false)"/>
            </v-card-title>

            <v-container>
                <v-form v-model="formData.valid" class="mb-3" validate-on="input lazy" @submit.prevent="addZone">
                    <v-alert v-if="errors.length"
                             class="mb-3 bg-error"
                             closable close-label="Close Alert"
                             density="compact" variant="tonal">
                        <p v-for="error in errors">{{ error }}</p>
                    </v-alert>
                    <v-row>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['name']" autofocus :rules="[vRules.required,vRules.maxLength(40)]"
                                          density="compact" label="Name"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-select v-model="formData['type']" :items="types" :rules="[vRules.required]"
                                      density="compact" item-title="name" item-value="id" label="Type"/>

                        </v-col>

                        <v-col cols="12" style="height: 300px">
                            <l-map ref="mapContainer"
                                   :center="[(formData.polygon_coordinates?.[0].lat??47.41322), (formData.polygon_coordinates?.[0].lng??-1.219482)]"
                                   :zoom="10" @ready="mapIsReady">
                                <l-tile-layer :url="$mapTiles.street"/>
                            </l-map>
                        </v-col>

                        <v-col class="py-0 text-right" cols="12">
                            <v-btn :disabled="!formData.valid" color="primary" size="small" type="submit">Save</v-btn>
                        </v-col>
                    </v-row>

                </v-form>
            </v-container>
        </v-card>
    </v-dialog>
</template>


<script setup>
import "leaflet/dist/leaflet.css";
import "leaflet-geosearch/assets/css/leaflet.css";

import "leaflet-draw";
import L from "leaflet";
import {reactive, ref} from "vue";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {LMap, LTileLayer} from "@vue-leaflet/vue-leaflet";
import {GeoSearchControl, OpenStreetMapProvider} from "leaflet-geosearch";
import {mapTiles} from "@/composables/mapConfig";

const emits = defineEmits(['update:modelValue', 'updateZones']);
const {showSnackbar} = useSnackbarStore();
const props = defineProps({
    modelValue: Boolean,
    location: Object,
    types: Array
});
const mapContainer = ref();
const errors = ref([]);
const dialogSwitch = ref(props.modelValue);

const formData = reactive({
    valid: false,
    name: null,
    type: null,
    polygon_coordinates: null,
});

function mapIsReady() {
    const map = mapContainer.value.leafletObject;
    map.attributionControl.remove();
    const geoSearchOptions = new GeoSearchControl({
        style: "bar",
        autoClose: true,
        keepResult: true,
        showMarker: false,
        searchLabel: "Enter address",
        provider: new OpenStreetMapProvider({searchUrl: mapTiles.searchUrl, reverseUrl: mapTiles.reverseUrl}),
    });

    map.addControl(geoSearchOptions);
    /*Add Draw Tool Bar Control*/
    const drawnItems = new L.FeatureGroup();
    map.addLayer(drawnItems);
    const drawControl = new L.Control.Draw({
        draw: {
            circle: false,
            polygon: {
                showArea: true,
                allowIntersection: false, // Restricts shapes to simple polygons
                drawError: {
                    color: '#9d1212', // Color the shape will turn when intersects
                    message: '<strong>Oh snap!<strong> you can\'t draw that!' // Message that will show when intersect
                }
            },
            // disable toolbar item by setting it to false
            marker: false,
            polyline: false,
            rectangle: false,
            circlemarker: false,
        },
        edit: {
            featureGroup: drawnItems
        }
    });
    map.addControl(drawControl);

    map.on('draw:created', function (e) {
        drawnItems.eachLayer(function (layer) {
            drawnItems.removeLayer(layer);
        });

        if (e.layerType === 'polygon') {
            formData['polygon_coordinates'] = e.layer.getLatLngs()[0];
        }
        drawnItems.addLayer(e.layer);
    });

    map.on('draw:edited', function (e) {
        e.layers.eachLayer(function (layer) {
            if (layer instanceof L.Polygon) {
                formData['polygon_coordinates'] = layer.getLatLngs()[0];
            }
        });
    });

    map.on('draw:deleted', function (e) {
        drawnItems.eachLayer(function (layer) {
            drawnItems.removeLayer(layer);
        });
        formData['polygon_coordinates'] = null;
    });
}

const addZone = () => {
    errors.value = [];
    send('post', `companies/locations/${props.location.id}/zones`, formData)
        .then((response) => {
            showSnackbar(response.data.message);
            if (response.data.success) {
                emits('updateZones', response.data.data);
                emits('update:modelValue', false);
            }
        })
        .catch(function (errorResponse) {
            console.log(errorResponse)
            if (errorResponse.response.data.errors) {
                let validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    errors.value.push(validationErrors[key][0]);
                });
            } else if (errorResponse.response.data.message) {
                errors.value.push(errorResponse.response.data.message);
            }
            showSnackbar('Something went wrong', 'error');
            document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
        })
}
</script>
